import Editor, { type Monaco, type OnMount } from '@monaco-editor/react';
import React, { forwardRef, useImperativeHandle, useState } from 'react';

export interface CodeEditorProps {
  /** CSS height */
  height: string;
  /** Language of the file. More languages are supported, but these are the ones with IntelliSense */
  language: 'typescript' | 'json' | 'css' | 'html';
  /** Starting code string for the editor */
  defaultValue: string;
  /** A cosmetic name for the file, like `custom-function.ts` */
  filename: string;
  /** Called when the code in the editor changes */
  onChange?: (value: string) => void;
  /** Good for modding the editor (e.g., adding global types to TypeScript, or a JSON schema to the JSON editor) */
  onMount?: OnMount;
}

export interface CodeEditorRef {
  /** Updates the editor value while preserving cursor position */
  setValue: (value: string) => void;
}

/**
 * VSCode-style editor with support for various languages
 *
 * @see https://github.com/suren-atoyan/monaco-react
 */
export const CodeEditor = forwardRef<CodeEditorRef, CodeEditorProps>(
  ({ height, language, defaultValue, filename, onChange, onMount }, ref) => {
    const [code, setCode] = useState<string>(defaultValue);
    const [editor, setEditor] = useState<
      Monaco['editor']['IStandaloneCodeEditor'] | null
    >(null);

    /**
     * Workaround to push external updates to the editor, such as resetting the value
     *
     * @see https://github.com/suren-atoyan/monaco-react/issues/402#issuecomment-1285600531
     */
    useImperativeHandle(ref, () => ({
      setValue: (value: string) => {
        if (!editor) return;
        editor.setValue(value);
      },
    }));

    const handleEditorChange = (value: string | undefined): void => {
      setCode(value ?? '');
      onChange?.(value ?? '');
    };

    const handleEditorDidMount: OnMount = (editor, monaco) => {
      setEditor(editor);
      onMount?.(editor, monaco);
    };

    return (
      <div style={{ height }}>
        <Editor
          defaultLanguage={language}
          defaultValue={code}
          path={`file:///${filename}`}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            minimap: {
              enabled: false,
            },
          }}
        />
      </div>
    );
  },
);
